import { ColumnDef } from '@tanstack/react-table'
import { DataTableProvider } from 'components/DataTable'
import {
  DataTableColumnSort,
  dataTableSortingStoreRepo,
} from 'components/DataTable/DataTableSorting/DataTableSortingStore'
import { IColumnSort, IColumnVisibility } from 'components/DataTable/types'
import {
  DECommonTable,
  DETableSearchComponent,
} from 'components/DataExpansionComponents/Common/CommonTable/CommonTable'
import { ExportModalProps } from 'components/Modals/ExportModal/ExportModal'
import { CompanyTypeSlug, TableTypeSlug } from 'models/companies'
import { StandardSummaryResponse } from 'models/paginated-response'
import { FilterIdentifier } from 'models/saved_view'
import React, { useMemo } from 'react'
import useApiService from 'services/api'
import { DoorVerticalRowConfig } from 'services/opportunities/unifiedDoorResponseAdapter'
import { VerticalRowConfig } from 'services/opportunities/unifiedResponseAdapter'
import { useUnifiedDoorsTableActions } from 'services/opportunities/useUnifiedDoorsTableActions'
import { useUnifiedTableActions } from 'services/opportunities/useUnifiedTableActions'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'

type ApiService = ReturnType<typeof useApiService>

/**
 * Opts every per-vertical `useXxxTableCols` hook accepts. Verified identical
 * across all six generic verticals (FIR-7727) — the shared shape is what lets a
 * single `<TargetTable>` drive any of them.
 */
export interface TargetTableColumnsHookOptions {
  sorting?: DataTableColumnSort[]
  inCampaigns?: boolean
}

/**
 * The legacy (flag-off) list/summary actions for a vertical. `<TargetTable>`
 * hands these to `useUnifiedTableActions`, which swaps in the unified endpoint
 * when the `enable_unified_opps_endpoint` flag is on — the flag lives one layer
 * below the component, so `<TargetTable>` itself never reads it.
 */
export interface TargetTableLegacyActions<T extends { id: number }> {
  listAction: (
    params: Record<string, any>,
    signal?: AbortSignal
  ) => Promise<{ results: T[] }>
  summaryAction: (
    params: Record<string, any>,
    signal?: AbortSignal
  ) => Promise<{ count: number; alt_count: number }>
}

/**
 * Declarative description of one opportunities target table (FIR-7727 / FIR-7728).
 *
 * Collapses the ~95–135 LOC of near-identical per-vertical boilerplate into
 * data + slots wired into the shared `<DECommonTable>`. Data fields configure
 * the shared plumbing (slugs, filter/preset identifiers, sort); slot fields
 * supply the vertical's columns/filterset/export modal and resolve its legacy
 * (flag-off) data actions from the api service.
 *
 * The optional `isDoorsTable` block (FIR-7728) lets the doors target tables
 * (lodging / convenience doors) reuse this abstraction: they render in
 * doors mode (alt_count stats + door-scoped actions) and fetch through the
 * unified DOORS endpoint instead of the chain endpoint.
 *
 * The restaurant (Chains) migration still needs richer seams that
 * `<DECommonTable>` does not yet support — `elasticSearchConfig?` (menu + name
 * Elasticsearch search), `customSummaryStats?` (LTV / pounds-per-year /
 * places-count header stats), and `menuModal?` (menu column + modal) — and is
 * gated on the FIR-6997 cutover; those are intentionally still left off.
 */
export interface TargetTableConfig<T extends { id: number } = { id: number }> {
  /** App vertical / sub-category slug, routed through `getTargetType`. */
  vertical: string
  /**
   * How to re-nest a flat unified row so the legacy columns/links resolve.
   * Chain verticals supply a `VerticalRowConfig` (`nestKey`); doors tables
   * supply a `DoorVerticalRowConfig` (`accessor`).
   */
  rowConfig: VerticalRowConfig | DoorVerticalRowConfig
  /** Filter store identifier (drives saved views + outgoing filter params). */
  filterIdentifier: FilterIdentifier
  /** Column-store saved-presets identifier (usually === `filterIdentifier`). */
  savedPresetsIdentifier: FilterIdentifier
  companyTypeSlug: CompanyTypeSlug
  tableTypeSlug: TableTypeSlug
  sortableFields: string[]
  /** Initial sort applied before the user picks a column. */
  defaultSort?: IColumnSort<object>[]
  /** Column-group display order in the column selector. */
  preferredGroupOrder: string[]
  searchPlaceholder: string
  /**
   * Initial column order seeded into the column store. Doors tables pin their
   * location columns up front; chain verticals leave this unset.
   */
  initialColumnOrder?: string[]
  /**
   * Base column visibility, merged UNDER the per-instance `defaultColumnVisibility`
   * prop (so a call site can still override). Doors tables ship a base map.
   */
  defaultColumnVisibility?: IColumnVisibility<any>
  /**
   * `baseFilters` key carrying the active campaign id passed to `useColumns` as
   * `inCampaigns`. Defaults to `'main_campaign'`; lodging-doors uses `'campaigns'`.
   */
  inCampaignsKey?: string
  // ---- doors mode (FIR-7728) — omitted for the generic chain verticals ----
  /**
   * Render in doors mode: header stat / pagination / select-all read the
   * distinct-parent-chain `alt_count`, actions are door-scoped, and data is
   * fetched through the unified DOORS endpoint (`useUnifiedDoorsTableActions`)
   * rather than the chain endpoint.
   */
  isDoorsTable?: boolean
  /** Accessor path to a row's chain-proxy id (doors), e.g. `'chain_proxy.id'`. */
  chainProxyIdAccessorKey?: string
  /** Accessor path to a row's door id (doors), e.g. `'id'`. */
  doorProxyIdAccessorKey?: string
  // ---- slots ----
  useColumns: (options: TargetTableColumnsHookOptions) => ColumnDef<T, any>[]
  Filterset: React.FC<{
    filterIdentifier: FilterIdentifier
    hideCampaignsFilter?: boolean
  }>
  ExportModal: React.FC<ExportModalProps>
  /**
   * Overrides the header search input (FIR-7728 seam). Omitted for every
   * current table (they use the plain `<TableSearch>`); the restaurant/Chains
   * migration will supply an Elasticsearch-backed search here.
   */
  SearchComponent?: DETableSearchComponent
  /**
   * Extra params merged into the list query + its query key (FIR-7728 seam). A
   * hook, so it can read dynamic out-of-band state — selected product ids today,
   * reusable for any future opp type that filters the list by state the table
   * itself doesn't own (forecast scenario, etc.). Omitted for every current table.
   */
  useExtraListParams?: () => Record<string, any>
  /**
   * Extra table-scoped modals (FIR-7728 seam) — e.g. the restaurant menu modal
   * and change-product-view modal — rendered inside the table container.
   */
  renderExtraModals?: React.ReactNode
  /** Resolve the vertical's legacy (flag-off) list/summary actions. */
  resolveLegacyActions: (api: ApiService) => TargetTableLegacyActions<T>
}

/** Per-instance props — everything that is NOT vertical config. */
export interface TargetTableProps {
  tableKey: string
  setTotalRowsCount?: (count: StandardSummaryResponse) => void
  pageParamName?: string
  baseFilters?: Record<string, any>
  filterIdentifierModifier?: FilterIdentifier
  hideCreateCompanyButton?: boolean
  AfterSearchComponent?: React.ReactNode
  defaultColumnVisibility?: IColumnVisibility<any>
}

/**
 * Identity helper that infers the row type `T` from a config literal — mirrors
 * the inference each per-vertical `<DECommonTable>` call site did inline. Lets a
 * vertical declare `export const xConfig = defineTargetTableConfig({ ... })`
 * without hand-writing the row generic.
 */
export function defineTargetTableConfig<T extends { id: number }>(
  config: TargetTableConfig<T>
): TargetTableConfig<T> {
  return config
}

// Stable fallback so a config without `useExtraListParams` calls a real (no-op)
// hook in the same order every render — keeps rules-of-hooks happy and avoids
// allocating a fresh params object per render.
const EMPTY_EXTRA_LIST_PARAMS: Record<string, any> = {}
const useNoExtraListParams = () => EMPTY_EXTRA_LIST_PARAMS

function TargetTableInner<T extends { id: number }>({
  config,
  ...props
}: { config: TargetTableConfig<T> } & Omit<
  TargetTableProps,
  'defaultColumnVisibility'
>) {
  const api = useApiService()
  const { tableKey, ...rest } = props
  const { sorting } = dataTableSortingStoreRepo.getStore(tableKey)()
  // Merge any config-supplied dynamic params (e.g. selected product ids) into
  // baseFilters so they reach both the list query and its query key. The hook is
  // always called (with a no-op fallback) to satisfy rules-of-hooks.
  const useExtraListParams = config.useExtraListParams ?? useNoExtraListParams
  const extraListParams = useExtraListParams()
  const baseFilters = useMemo(
    () => ({ ...props.baseFilters, ...extraListParams }),
    [props.baseFilters, extraListParams]
  )
  const columns = config.useColumns({
    sorting,
    inCampaigns: baseFilters[config.inCampaignsKey ?? 'main_campaign'],
  })
  // Resolve once per (config, api) rather than allocating a fresh actions object
  // every render. `config` is a stable module-level constant and `api` is stable,
  // so this is computed once; it also gives the actions hooks a stable
  // legacy-actions reference should they ever key their memo on it.
  const legacyActions = useMemo(
    () => config.resolveLegacyActions(api),
    [config, api]
  )
  // Both hooks are called unconditionally (rules of hooks) and only the one
  // matching the config's mode is used; the unused hook just memoizes action
  // closures it never runs. Chain configs carry a `VerticalRowConfig` (nestKey),
  // doors configs a `DoorVerticalRowConfig` (accessor) — the cast feeds each hook
  // the shape it expects.
  const chainActions = useUnifiedTableActions<T>(
    config.vertical,
    config.rowConfig as VerticalRowConfig,
    legacyActions
  )
  const doorActions = useUnifiedDoorsTableActions<T>(
    config.vertical,
    config.rowConfig as DoorVerticalRowConfig,
    legacyActions
  )
  const { listAction, summaryAction } = config.isDoorsTable
    ? doorActions
    : chainActions

  return (
    <DECommonTable
      {...rest}
      tableKey={tableKey}
      baseFilters={baseFilters}
      columns={columns}
      sortableFields={config.sortableFields}
      filterIdentifier={config.filterIdentifier}
      apiGetListAction={listAction}
      apiGetSummaryAction={summaryAction}
      companyTypeSlug={config.companyTypeSlug}
      tableTypeSlug={config.tableTypeSlug}
      defaultSort={config.defaultSort}
      searchPlaceholder={config.searchPlaceholder}
      FilterSet={config.Filterset}
      ExportModal={config.ExportModal}
      SearchComponent={config.SearchComponent}
      renderExtraModals={config.renderExtraModals}
      isDoorsTable={config.isDoorsTable}
      chainProxyIdAccessorKey={config.chainProxyIdAccessorKey}
      doorProxyIdAccessorKey={config.doorProxyIdAccessorKey}
    />
  )
}

/**
 * Config-driven opportunities target table (FIR-7727).
 *
 * Absorbs the two-layer provider/component skeleton every generic vertical
 * table repeated: `<ColumnsStoreProvider>` + `<DataTableProvider>` wrapping a
 * component that wires the vertical's columns/filterset/legacy-actions into
 * `<DECommonTable>`. Render a vertical with its co-located config:
 *
 *   <TargetTable config={universitiesTableConfig} tableKey={...} ... />
 */
export function TargetTable<T extends { id: number }>({
  config,
  tableKey,
  defaultColumnVisibility,
  ...rest
}: { config: TargetTableConfig<T> } & TargetTableProps) {
  // A config's base visibility map (doors tables) sits UNDER the per-instance
  // prop so a call site can still override individual columns. Chain verticals
  // carry no base map, so the raw prop passes through unchanged.
  const mergedColumnVisibility = config.defaultColumnVisibility
    ? { ...config.defaultColumnVisibility, ...defaultColumnVisibility }
    : defaultColumnVisibility
  return (
    <ColumnsStoreProvider
      tableKey={tableKey}
      defaultColumnVisibility={mergedColumnVisibility}
      initialColumnOrder={config.initialColumnOrder}
      savedPresetsIdentifier={config.savedPresetsIdentifier}
      preferredGroupOrder={config.preferredGroupOrder}
    >
      <DataTableProvider tableKey={tableKey}>
        <TargetTableInner config={config} tableKey={tableKey} {...rest} />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
